import gatewayApi from "./gatewayAxios";
import { initSession } from "./cryptoService";
import { errorCodeOf, isSessionExpired } from "./gatewayErrors";
import { saveDeviceSecret } from "./deviceSecret";

// New-device sign-in, second half. The sign-in call answered GWD001 with a challengeId and SMSed an OTP; these
// calls (outside gatewayApi's /api/bpay base, hence absolute URLs; the interceptors still add the crypto session
// and encrypt the body) finish it. There is no login token yet, so no Authorization header.

function post(path, data) {
  return gatewayApi({
    method: "POST",
    url: `${process.env.REACT_APP_GATEWAY_URL}${path}`,
    data,
    headers: { "Content-Type": "application/json" },
  }).then((res) => res.data);
}

// The crypto session is checked before the OTP is looked at, so retrying after a re-handshake costs the user nothing.
async function withSessionRetry(call) {
  try {
    return await call();
  } catch (err) {
    if (isSessionExpired(err)) {
      await initSession();
      return call();
    }
    throw err;
  }
}

// Resolves to the normal sign-in response plus deviceSecret (which is remembered for the next sign-in).
export async function verifyDevice({ challengeId, otp, mpin }) {
  // A trusted device's challenge (GWD010) has no OTP, only the MPIN.
  const data = { challengeId, ...(otp ? { otp } : {}), ...(mpin ? { mpin } : {}) };
  const res = await withSessionRetry(() => post("/auth/device/verify", data));
  if (res?.deviceSecret) saveDeviceSecret(res.deviceSecret);
  return res;
}

// First login without an MPIN. After the device OTP the gateway answers GWD012 ("MPIN setup required"); then:
//   setDeviceMpin        the new MPIN typed twice -> a second OTP is SMSed (nothing is saved yet)
//   confirmDeviceMpinOtp that OTP -> the MPIN is saved ("MPIN set successfully")
// and the MPIN is then asked for once more through verifyDevice({ challengeId, mpin }).
export function setDeviceMpin({ challengeId, mpin, confirmMpin }) {
  return withSessionRetry(() => post("/auth/device/mpin-setup", { challengeId, mpin, confirmMpin }));
}

export function confirmDeviceMpinOtp({ challengeId, otp }) {
  return withSessionRetry(() => post("/auth/device/mpin-setup/confirm", { challengeId, otp }));
}

// A full device list (GWD015) or full sessions (GWB006) is not a refusal: after the OTP and the MPIN the user chooses one to give up.
//   removeTrustedDevice  stop trusting that device (its open sessions are logged out); the login then carries on
//   endLoginSession      log that session out; the login then carries on
// Either answers like verifyDevice (the login, plus the new device's secret) or, if something is still full, with the next choice.
export async function removeTrustedDevice({ challengeId, deviceId }) {
  const res = await withSessionRetry(() => post("/auth/device/remove", { challengeId, deviceId }));
  if (res?.deviceSecret) saveDeviceSecret(res.deviceSecret);
  return res;
}

export async function endLoginSession({ challengeId, sessionId }) {
  const res = await withSessionRetry(() => post("/auth/device/end-session", { challengeId, sessionId }));
  if (res?.deviceSecret) saveDeviceSecret(res.deviceSecret);
  return res;
}

export const MPIN_SETUP_NOTICE = "MPIN setup required. Please set your MPIN.";
const RESTART = "Something went wrong. Please log in again.";

/**
 * What a device-verify style answer means for the screens. One of:
 *   { stage, patch, notice }   go to that step's page (patch is merged into the flow state)
 *   { signin }                 the login itself (without the device secret, which was already remembered)
 *   { problem }                an answer that cannot be shown (a choice with nothing to choose from)
 */
export function nextStepAfter(res) {
  switch (res?.code) {
    case "GWD010":
      return { stage: "MPIN_CONFIRM", patch: {}, notice: "OTP verified. Now enter your MPIN." };
    case "GWD012":
      return { stage: "MPIN_SETUP", patch: {}, notice: MPIN_SETUP_NOTICE };
    case "GWD015":
      if (!Array.isArray(res.data?.devices) || res.data.devices.length === 0) return { problem: RESTART };
      return {
        stage: "CHOOSE_DEVICE",
        patch: { choice: res.data },
        notice: "Your OTP and MPIN are verified. One device must be removed before you can continue.",
      };
    case "GWB006":
      if (!Array.isArray(res.data?.sessions) || res.data.sessions.length === 0) return { problem: RESTART };
      return {
        stage: "CHOOSE_SESSION",
        patch: { choice: res.data },
        notice: "Your MPIN is verified. One session must be logged out before you can continue.",
      };
    default: {
      const { deviceSecret, ...signin } = res || {};
      return { signin };
    }
  }
}

export function resendDeviceOtp({ challengeId }) {
  return withSessionRetry(() => post("/auth/device/resend", { challengeId }));
}

// action: "retry" (stay on the screen), "restart" (go back to the login form), "locked" (MPIN lockout).
export function describeDeviceError(err) {
  const serverMessage = err?.response?.data?.message;
  switch (errorCodeOf(err)) {
    case "GWD002": return { action: "retry", message: "Invalid OTP." };
    case "GWA009": return { action: "retry", message: "MPIN and confirm MPIN do not match." };
    case "GWD013": return { action: "restart", message: "That step is no longer available. Please log in again." };
    case "GWA007": return { action: "retry", message: "MPIN must be 4 to 6 digits." };
    case "GWA005": return { action: "retry", message: "Invalid MPIN." };
    case "GWA006": return { action: "locked", message: serverMessage || "Account locked. Try again later." };
    case "GWD007": return { action: "retry", message: "Please wait a minute before requesting another OTP." };
    case "GWD005": return { action: "retry", message: "Could not send the OTP. Please try again." };
    case "GWD009": return { action: "retry", message: "Device verification is temporarily unavailable. Please try again shortly." };
    case "GWD014": return { action: "retry", message: "That device is no longer on your list. Pick another one." };
    case "GWB005": return { action: "retry", message: "That session has already ended. Pick another one." };
    case "GWD003": return { action: "restart", message: "Verification expired. Please log in again." };
    case "GWD004": return { action: "restart", message: "Too many wrong OTP attempts. Please log in again." };
    case "GWD006": return { action: "restart", message: "Device limit reached. Remove a device or contact support." };
    case "GWB004": return { action: "restart", message: "Maximum active sessions reached. Please log out from another device and try again." };
    case "GWD011": return { action: "restart", message: "Too many OTP requests from this device. Please try again after 15 minutes." };
    case "GWD008": return { action: "restart", message: "Too many OTP requests. Please try again later." };
    case "GWA002": return { action: "restart", message: "Account is inactive. Contact your administrator." };
    default: return { action: "retry", message: serverMessage || "Something went wrong. Please try again." };
  }
}
